import { TRACKER_TYPES } from '@racemap/sdk/schema/trackers';
import type { DataNode } from 'antd/es/tree';
import type { Key } from 'react';
import { EventsNode } from './EventsNode';
import { FilterNode } from './FilterNode';
import {
  ActivationFilters,
  BatteryFilters,
  Filters,
  LastAtFilters,
  LocationFilters,
  LogisticsFilters,
  MessageFilters,
  OnlineFilters,
} from './FilterTreeFunctions';
import { TagFilterNode } from './TagFilterNode';
import { UsersFilterNode } from './UsersFilterNode';
import { prepareTitle } from './prepareTitle';

export const FilterLabels: Record<Filters, string> = {
  [Filters.Tags]: 'Tags',
  [Filters.Events]: 'Events',
  [Filters.Online]: 'Connection',
  [Filters.TrackerType]: 'Type',
  [Filters.Messages]: 'Messages',
  [Filters.Location]: 'Location',
  [Filters.Battery]: 'Battery',
  [Filters.Activation]: 'State',
  [Filters.Logistics]: 'Logistics',
  [Filters.LastDataAt]: 'Last Data At',
  [Filters.LastConnectedAt]: 'Last Connected At',
  [Filters.LastDisconnectedAt]: 'Last Disconnected At',
  [Filters.Users]: 'Users',
};

const FilterHints: Partial<Record<Filters, string>> = {
  [Filters.Tags]:
    'Tags are used to group trackers. Hover over a tag to view its details or remove trackers from it. You can also drag and drop trackers onto a tag to assign them. Additionally, tags themselves can be dragged to move all associated trackers into a selection.',
};

export interface DataNodeProps {
  key: Key;
  title?: React.ReactNode | ((data: DataNode) => React.ReactNode);
}

export interface FilterNodeProps {
  key: Filters;
  title?: React.ReactNode | ((data: DataNode) => React.ReactNode);
  filterLabels?: Record<string, string>;
  filters: Record<string, string>;
}

const BatteryFilterLabels: Record<BatteryFilters, string> = {
  [BatteryFilters.FromEmtpyTo15]: 'Almost empty',
  [BatteryFilters.From15To25]: '15%-25%',
  [BatteryFilters.From25To50]: '25%-50%',
  [BatteryFilters.From50To75]: '50%-75%',
  [BatteryFilters.From75To90]: '75%-90%',
  [BatteryFilters.From90ToFull]: 'Full',
};

const ActivationFilterLabels: Record<ActivationFilters, string> = {
  [ActivationFilters.ARCHIVED]: 'Archived',
  [ActivationFilters.UNUSED]: 'Unused',
  [ActivationFilters.USED]: 'Used',
  [ActivationFilters.RACEMAPTRACKER]: 'Racemap Tracker',
};

const LocationFilterLabels: Record<LocationFilters, string> = {
  [LocationFilters.Cell]: 'Mobilcell based',
  [LocationFilters.NoFix]: 'GPS No Fix',
  [LocationFilters.Fix]: 'GPS Fix',
  [LocationFilters.NoLocation]: 'No location',
};

const LastDataAtFilterLabels: Record<LastAtFilters, string> = {
  [LastAtFilters.Recently]: 'Recently',
  [LastAtFilters.From1MinuteTo5Minutes]: '1min-5min',
  [LastAtFilters.From5MinutesTo30Minutes]: '5min-30min',
  [LastAtFilters.MoreThan30Minutes]: '>30min',
};

export const LogisticsFilterLabels: Record<LogisticsFilters, string> = {
  [LogisticsFilters.IN_STOCK]: 'In stock',
  [LogisticsFilters.OUT_OF_STOCK]: 'Out of stock',
};

const OnlineFilterLabels: Record<OnlineFilters, string> = {
  [OnlineFilters.Connected]: 'Connected with Racemap',
  [OnlineFilters.Data]: 'Dataconnection',
  [OnlineFilters.Registered]: 'At Mobilenetwork registered',
  [OnlineFilters.Offline]: 'No connection',
};

const MessagesFilterLabels: Record<MessageFilters, string> = {
  [MessageFilters.NO_NEW_MESSAGES]: 'No new message',
  [MessageFilters.WITH_ERROR_MESSAGES]: 'With error messages',
  [MessageFilters.WITH_PENDING_MESSAGES]: 'With pending messages',
  [MessageFilters.WITH_SCHEDULED_MESSAGES]: 'With scheduled Messages',
};

export const prepareFilterSubTreeComponentMapping = (): Record<Filters, DataNode> => {
  return {
    [Filters.Tags]: TagFilterNode({
      key: Filters.Tags,
      title: prepareTitle(Filters.Tags, FilterLabels[Filters.Tags], FilterHints[Filters.Tags]),
    }),
    [Filters.Events]: EventsNode({
      key: Filters.Events,
      title: prepareTitle(Filters.Events, FilterLabels[Filters.Events]),
    }),
    [Filters.Online]: FilterNode({
      key: Filters.Online,
      title: prepareTitle(Filters.Online, FilterLabels[Filters.Online]),
      filterLabels: OnlineFilterLabels,
      filters: OnlineFilters,
    }),
    [Filters.TrackerType]: FilterNode({
      key: Filters.TrackerType,
      title: prepareTitle(Filters.TrackerType, FilterLabels[Filters.TrackerType]),
      filters: TRACKER_TYPES,
    }),
    [Filters.Messages]: FilterNode({
      key: Filters.Messages,
      title: prepareTitle(Filters.Messages, FilterLabels[Filters.Messages]),
      filterLabels: MessagesFilterLabels,
      filters: MessageFilters,
    }),
    [Filters.Location]: FilterNode({
      key: Filters.Location,
      title: prepareTitle(Filters.Location, FilterLabels[Filters.Location]),
      filterLabels: LocationFilterLabels,
      filters: LocationFilters,
    }),
    [Filters.Battery]: FilterNode({
      key: Filters.Battery,
      title: prepareTitle(Filters.Battery, FilterLabels[Filters.Battery]),
      filterLabels: BatteryFilterLabels,
      filters: BatteryFilters,
    }),
    [Filters.Activation]: FilterNode({
      key: Filters.Activation,
      title: prepareTitle(Filters.Activation, FilterLabels[Filters.Activation]),
      filterLabels: ActivationFilterLabels,
      filters: ActivationFilters,
    }),
    [Filters.Logistics]: FilterNode({
      key: Filters.Logistics,
      title: prepareTitle(Filters.Logistics, FilterLabels[Filters.Logistics]),
      filterLabels: LogisticsFilterLabels,
      filters: LogisticsFilters,
    }),
    [Filters.LastDataAt]: FilterNode({
      key: Filters.LastDataAt,
      title: prepareTitle(Filters.LastDataAt, FilterLabels[Filters.LastDataAt]),
      filterLabels: LastDataAtFilterLabels,
      filters: LastAtFilters,
    }),
    [Filters.Users]: UsersFilterNode({
      key: Filters.Users,
      title: prepareTitle(Filters.Users, FilterLabels[Filters.Users]),
    }),
    [Filters.LastConnectedAt]: FilterNode({
      key: Filters.LastConnectedAt,
      title: prepareTitle(Filters.LastConnectedAt, FilterLabels[Filters.LastConnectedAt]),
      filterLabels: LastDataAtFilterLabels,
      filters: LastAtFilters,
    }),
    [Filters.LastDisconnectedAt]: FilterNode({
      key: Filters.LastDisconnectedAt,
      title: prepareTitle(Filters.LastDisconnectedAt, FilterLabels[Filters.LastDisconnectedAt]),
      filterLabels: LastDataAtFilterLabels,
      filters: LastAtFilters,
    }),
  };
};
